/**
 * API fetch wrapper — the dispatcher surface's HTTP layer.
 *
 * Auth model (matches the live Worker):
 *  - Login returns `{ user, accessToken }` in the BODY. The access token is
 *    held in memory (module state) — never localStorage — and sent as
 *    `Authorization: Bearer <token>`.
 *  - The server also sets an HttpOnly `refresh_token` cookie. When a request
 *    401s, we attempt ONE automatic refresh via POST /auth/refresh (which
 *    reads that cookie) and replay the original request.
 *  - Single-flight refresh: concurrent 401s share one refresh promise, then
 *    each replays its own original request. A failed refresh clears the
 *    session and fires `onUnauthorized`.
 */

import type { UserRole } from "@eagledispatch/shared";

/**
 * The session-visible identity. Login returns a full wire `User` (name, phone,
 * etc.); `/auth/me` returns the JWT payload (only `userId`, `tenantId`, `role`,
 * …). The app routes purely on `role`, so the session user is modelled as the
 * common subset — both shapes satisfy it.
 */
export interface SessionUser {
  role: UserRole;
  name?: string | null;
  username?: string | null;
  /** Present for DRIVER sessions (signed into the access token at pin-login). */
  driverId?: string | null;
  /**
   * Unit #19: per-dispatcher create toggle. Denormalized into the JWT
   * at sign time so the UI can gate the dispatcher NEW JOB button
   * without a separate fetch. Always present (server-defaulted to
   * `true` for non-dispatcher roles; the UI gates only on
   * `role === "DISPATCHER" && canCreateJobs === false`).
   */
  canCreateJobs?: boolean;
  /**
   * Unit #20: three more per-dispatcher toggles for in-form reference
   * creation. Same UX gating pattern as canCreateJobs.
   */
  canCreateCustomers?: boolean;
  canCreateProperties?: boolean;
  canCreateLandfills?: boolean;
}

const API_URL =
  import.meta.env.VITE_API_URL ||
  "https://api.eagledispatch.work";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    super(
      typeof body.message === "string"
        ? body.message
        : body.error === "validation"
          ? "Validation failed"
          : `Request failed with status ${status}`,
    );
    this.name = "ApiError";
    this.status = status;
    this.code = typeof body.error === "string" ? body.error : "unknown";
    this.details = body.details ?? null;
    this.body = body;
  }

  /**
   * Field-level validation errors as a flat map: field name → first message.
   * The API returns `{ error: "validation", details: { fieldErrors: {...} } }`.
   */
  get fieldErrors(): Record<string, string> {
    const fe = (
      this.details as { fieldErrors?: Record<string, string[]> } | null
    )?.fieldErrors;
    if (!fe) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(fe)) {
      if (Array.isArray(v) && v.length > 0) out[k] = v[0];
    }
    return out;
  }

  /**
   * ISO-8601 retry moment carried by the E/R cooldown gate 400. The finish
   * endpoint returns `{ error: "gate_failed", retryAt }` at the BODY top level
   * when the return drive is still locked. Extracted here (rather than by
   * poking `body` in the caller) so the gate's response contract has one
   * typed home. Null when this error carries no cooldown payload.
   */
  get retryAt(): string | null {
    const v = this.body?.retryAt;
    return typeof v === "string" && v.length > 0 ? v : null;
  }
}

/** SPEC-66a: human message for a 429 rate_limited ApiError, or null when
 *  the error is anything else. Callers check FIRST in their catch blocks.
 *  The seconds guard is integer + 1..3600 (SPEC-66a audit BUG #1): the
 *  server always emits Math.max(1, Math.ceil(ms/1000)) so integers only,
 *  and anything beyond an hour is a misconfiguration, not an actionable
 *  wait — both fall back to "a minute" rather than rendering "Infinitys". */
export function rateLimitMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 429 || err.code !== "rate_limited") {
    return null;
  }
  const sec = err.body?.retryAfterSec;
  const safe = typeof sec === "number" && Number.isInteger(sec) && sec >= 1 && sec <= 3600;
  const wait = safe ? `${sec}s` : "a minute";
  return `Too many attempts. Wait ${wait} and try again.`;
}

export interface Session {
  user: SessionUser;
  accessToken: string;
}

type AuthState =
  | { status: "loading" }
  | { status: "authed"; session: Session }
  | { status: "unauthed" };

let authState: AuthState = { status: "loading" };

/**
 * Notified when a refresh fails or the session is cleared mid-use.
 *
 * SPEC-71 §3C widens the signature (backward-compatible — an arity-0 listener
 * is still assignable): `reason` is the machine code behind a KICK, and
 * `intentional` distinguishes a user-initiated logout/Clock Out (no error
 * surface) from a kick.
 */
type UnauthorizedListener = (reason?: string, intentional?: boolean) => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Single-flight refresh promise; null when no refresh is in progress. */
let refreshPromise: Promise<RefreshOutcome> | null = null;

/**
 * SPEC-71 §3C — the machine code of the most recent KICK (a session end that
 * was NOT an intentional logout), or null when none has occurred / it has been
 * consumed. Durable backstop for the login surfaces: the App-level toast
 * cannot survive a `/driver` `key={epoch}` remount, so `Login`/`DriverLogin`
 * each render this line once.
 *
 * Clear-set (precise):
 *  - SET by `notifyUnauthorized(reason)` when called WITHOUT `intentional ===
 *    true`; the value is `reason ?? null` (a kick with no code stores null —
 *    the login line is omitted, the toast still fires the default copy).
 *  - CLEARED by (a) `setSession` — every successful auth entry point (login,
 *    pinLogin, finishWebauthnLogin) goes through it — and (b)
 *    `clearLastKickReason()`, which the two login surfaces call after their
 *    first render ("displayed once").
 *  - UNTOUCHED by `logout()` (it passes `intentional: true`), so a deliberate
 *    sign-out never raises the login line.
 *
 * Deliberately NOT set by `bootstrapSession`'s terminal refresh: the boot path
 * has no UI yet and today's UX (straight to the login screen, no toast) is
 * preserved (spec §3C "do-not-fix" note).
 */
let lastKickReasonCode: string | null = null;

/** The durable kick reason (machine code) or null. See the clear-set above. */
export function lastKickReason(): string | null {
  return lastKickReasonCode;
}

/** Clear the durable kick reason — the login surfaces call this after their
 *  first render so the line is displayed exactly once. */
export function clearLastKickReason(): void {
  lastKickReasonCode = null;
}

/** Subscribe to session-expiry events (used by the root component to route). */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized(reason?: string, intentional?: boolean): void {
  if (!intentional) {
    lastKickReasonCode = reason ?? null;
  }
  authState = { status: "unauthed" };
  for (const listener of unauthorizedListeners) {
    try {
      listener(reason, intentional);
    } catch {
      // Listener errors must not break the caller's control flow.
    }
  }
}

async function parseBody(res: Response): Promise<Record<string, unknown>> {
  try {
    const body = (await res.json()) as Record<string, unknown>;
    return body ?? {};
  } catch {
    return {};
  }
}

/**
 * SPEC-71 §3C (BUG #4) — the refresh outcome. ONLY a `401` is proof the session
 * is dead (terminal). Every other non-OK (429, 5xx, 403, 400, …) THROWS out of
 * `doRefresh`, so `rawRequest`'s catch converts it to a retryable
 * `ApiError(0, network)` and the session + caller form state survive.
 */
type RefreshOutcome =
  | { ok: true; accessToken: string }
  | { ok: false; code: string };

/**
 * Exchange the refresh cookie for a new access token.
 *
 * SPEC-71 §3C (BUG #4) — the terminal/retryable split:
 *  - network failure → THROW (unchanged; retryable — the cookie is intact).
 *  - `401` → parse the body, return terminal `{ ok: false, code }` where `code`
 *    is `body.error` when a string, else `"unauthorized"` (unparsable body).
 *  - any other non-OK (429 / 5xx / 403 / 400 / …) → THROW. A 5xx or a WAF 403
 *    is NOT `fetch()` failure but is also NOT proof of death; the old code
 *    collapsed these into the kick path and silently torched live sessions.
 *  - 2xx with a usable token → `{ ok: true, accessToken }`; a 2xx without one
 *    is a malformed success → THROW (retryable, never a silent kick).
 *
 * Does NOT mutate authState — callers decide how to apply the result.
 */
async function doRefresh(): Promise<RefreshOutcome> {
  const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
    // SPEC-66 §2.7: inline fetch bypasses buildHeaders — POST needs the CSRF header.
    headers: { "X-Eagle-CSRF": "1" },
  });

  // Only 401 is proof of death. Everything else non-OK is retryable.
  if (res.status === 401) {
    const body = await parseBody(res);
    const code = typeof body.error === "string" ? body.error : "unauthorized";
    return { ok: false, code };
  }
  if (!res.ok) {
    throw new ApiError(res.status, {
      error: "network",
      message: "Could not refresh session — server unavailable. Retry shortly.",
    });
  }

  const body = await parseBody(res);
  const accessToken = body.accessToken;
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    // A 2xx that carries no token is a malformed success, not a death
    // certificate — retryable (never a silent kick).
    throw new ApiError(0, {
      error: "network",
      message: "Refresh returned no token — retry shortly.",
    });
  }
  return { ok: true, accessToken };
}

/**
 * Single-flight wrapper around doRefresh. Concurrent callers share one call,
 * so the promise identity every caller awaits is unchanged (SPEC-71 §3C hard
 * part: the single-flight promise still returns what callers await).
 */
function refreshOnce(): Promise<RefreshOutcome> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Apply a freshly-refreshed token to the current session (mid-session path). */
function applyAccessToken(accessToken: string): void {
  if (authState.status === "authed") {
    authState = {
      status: "authed",
      session: { ...authState.session, accessToken },
    };
  }
}

/**
 * The core fetch primitive. Returns the RAW response (both 2xx and non-2xx);
 * throws only on network failure or a rejected refresh. On a 401 (when we hold
 * a session) it performs at most one refresh + replay; if the replay still
 * 401s, it clears the session and fires `onUnauthorized`.
 *
 * Exposed separately from `apiFetch` so the offline queue can classify the
 * raw status code (2xx / cooldown-400 / other-4xx / 5xx) instead of going
 * through `apiFetch`'s throw-on-non-2xx path.
 */
export async function rawRequest(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const buildHeaders = (token?: string): Headers => {
    const headers = new Headers(options.headers);
    // SPEC-66 §2.7: CSRF custom header — the offline queue replays through
    // rawRequest, so queued actions inherit it for free.
    headers.set("X-Eagle-CSRF", "1");
    // Only default to JSON when the body is a JSON string. FormData (multipart
    // photo upload) must set its own boundary — forcing application/json here
    // corrupts the multipart body.
    if (
      !headers.has("Content-Type") &&
      typeof options.body === "string"
    ) {
      headers.set("Content-Type", "application/json");
    }
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  };

  const current =
    authState.status === "authed" ? authState.session.accessToken : undefined;

  let res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: buildHeaders(current),
  });

  if (res.status === 401 && authState.status === "authed") {
    let outcome: RefreshOutcome;
    try {
      outcome = await refreshOnce();
    } catch {
      // Network failure during refresh (offline, DNS, CORS blip) — OR a
      // non-401 non-OK status (429/5xx/403/400) which doRefresh throws — is
      // NOT proof the session expired. The refresh cookie is still intact, we
      // just couldn't confirm liveness. Surfacing a typed, retryable error
      // (status 0) lets the caller keep its form state and retry on the next
      // request instead of torching the session over a transient blip.
      throw new ApiError(0, {
        error: "network",
        message: "Network error — could not refresh session. Check your connection and retry.",
      });
    }
    if (outcome.ok) {
      const freshToken = outcome.accessToken;
      applyAccessToken(freshToken);
      res = await fetch(`${API_URL}${path}`, {
        ...options,
        credentials: "include",
        headers: buildHeaders(freshToken),
      });
      if (res.status === 401) {
        // SPEC-71 §3C — the replay ALSO 401'd. This is the terminal proof:
        // a 401 is content-free (an unparsable body), so the generic copy.
        notifyUnauthorized("unauthorized");
      }
    } else {
      // SPEC-71 §3C — the refresh itself 401'd with a machine code. Kick
      // carrying that reason so the UI can explain WHY.
      notifyUnauthorized(outcome.code);
      throw new ApiError(401, { error: outcome.code });
    }
  }

  return res;
}

/**
 * The typed fetch: rawRequest + JSON parse / error mapping. Throws `ApiError`
 * on any non-2xx (with the parsed body attached).
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await rawRequest(path, options);

  if (!res.ok) {
    const body = await parseBody(res);
    throw new ApiError(res.status, body);
  }

  // 204 No Content and other empty bodies parse to undefined.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Session lifecycle ────────────────────────────────────────────────────────

/** Bootstrap the session from a login response body (full wire User). */
export function setSession(user: SessionUser, accessToken: string): void {
  authState = { status: "authed", session: { user, accessToken } };
  // SPEC-71 §3C clear-set: every successful auth entry point (login,
  // pinLogin, finishWebauthnLogin, passkey) calls this — a fresh sign-in
  // clears the durable kick reason so the login line never leaks across
  // sessions.
  lastKickReasonCode = null;
}

/** Return the current session (or null when not authenticated). */
export function getSession(): Session | null {
  return authState.status === "authed" ? authState.session : null;
}

/**
 * Clear the in-memory session. Callers also POST /auth/logout when online.
 *
 * SPEC-71 §3C / BUG #5 — `intentional: true`: a user-initiated sign-out or
 * Clock Out completes with NO kick toast and does NOT set the durable login
 * line (the listener suppresses the error surface when `intentional`).
 */
export function logout(): void {
  authState = { status: "unauthed" };
  notifyUnauthorized(undefined, true);
}

/**
 * Attempt to restore a session on app boot using the refresh cookie. Returns
 * the session when the cookie is still valid, otherwise null (→ login screen).
 *
 * Boot path differs from the mid-session path: /auth/refresh returns only a
 * token (no user), so we then GET /auth/me with that token to rebuild the
 * full session. Both steps must succeed.
 */
export async function bootstrapSession(): Promise<Session | null> {
  // SPEC-71 §3C do-not-fix: bootstrap calls refreshOnce() directly, NOT
  // through rawRequest's catch. A non-401 non-OK (429/5xx) now THROWS out of
  // doRefresh here too — it propagates to App boot's `.catch` → unauthed,
  // exactly the same UX as today. A terminal 401 returns `{ ok: false }`.
  const outcome = await refreshOnce();
  if (!outcome.ok) {
    authState = { status: "unauthed" };
    return null;
  }
  const accessToken = outcome.accessToken;

  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/auth/me`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        // SPEC-66 §2.7: GET is server-exempt; set for uniformity.
        "X-Eagle-CSRF": "1",
      },
    });
  } catch {
    authState = { status: "unauthed" };
    return null;
  }

  if (!res.ok) {
    authState = { status: "unauthed" };
    return null;
  }

  // /auth/me returns the verified JWT session payload (`role`, `userId`, …),
  // not the full wire User. The app only needs `role` to route.
  const body = (await res.json()) as { user: SessionUser };
  const session: Session = { user: body.user, accessToken };
  authState = { status: "authed", session };
  return session;
}

if (typeof window !== "undefined") {
  window.addEventListener("eagleForeground", () => {
    void refreshOnce().catch(() => {});
  });
  window.addEventListener("focus", () => {
    void refreshOnce().catch(() => {});
  });
}

export { API_URL };
